import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { camelToSnake, snakeToCamel } from "caseparser";
import { User } from "../auth/auth";
import { objectToFormData } from "@/shared/utils/data";

export const chatApi = api
  .enhanceEndpoints({
    addTagTypes: ["contacts", "conversations", "messages", "managers"],
  })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      contacts: builder.query({
        query: ({ search }) => ({
          url: `chat/contacts`,
          method: RequestMethod.GET,
          params: camelToSnake({
            search,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["contacts"],
      }),
      managerConversations: builder.query({
        query: () => ({
          url: `chat/manager-conversations`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["conversations"],
      }),
      supportConversations: builder.query({
        query: () => ({
          url: `chat/support-conversations`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["conversations"],
      }),
      messages: builder.query({
        query: ({ conversationId }) => ({
          url: `chat/conversations/${conversationId}/messages`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["messages"],
      }),
      createMessage: builder.mutation({
        query: ({
          conversationId,
          content,
          files,
          clientId,
          managerId,
          senderId,
        }) => {
          return {
            url: `chat/messages`,
            method: RequestMethod.POST,
            body: objectToFormData(
              camelToSnake({
                files,
                content,
                clientId,
                managerId,
                senderId,
                conversationId,
              })
            ),
            formData: true,
          };
        },
        transformResponse: (response: any) => snakeToCamel(response.data),
      }),
      createSupportConversation: builder.mutation({
        query: () => ({
          url: `chat/support-conversations`,
          method: RequestMethod.POST,
        }),
      }),
      updateConversation: builder.mutation({
        query: ({ conversationId, status }) => ({
          url: `/chat/conversations/status`,
          method: RequestMethod.POST,
          body: camelToSnake({ status, conversationId }),
        }),
        invalidatesTags: ["conversations"],
      }),
      managers: builder.query({
        query: () => ({
          url: `get-active-managers`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any): Array<User> =>
          snakeToCamel(response.data),
        providesTags: ["managers"],
      }),
      assignManager: builder.mutation({
        query: ({ managerId, conversationId, senderId }) => ({
          url: `/chat/conversations/manager`,
          method: RequestMethod.POST,
          body: camelToSnake({
            managerId,
            conversationId,
            senderId,
          }),
        }),
        transformResponse: (response: any): Array<User> =>
          snakeToCamel(response.data),
        invalidatesTags: ["conversations"],
      }),
    }),
  });

export const {
  useContactsQuery,
  useMessagesQuery,
  useManagerConversationsQuery,
  useSupportConversationsQuery,
  useCreateMessageMutation,
  useCreateSupportConversationMutation,
  useUpdateConversationMutation,
  useManagersQuery,
  useAssignManagerMutation,
} = chatApi;
